import { ReactElement, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { keyBy, toNumber } from 'lodash-es'
import { Stack } from '@mui/material'
import type { ShearConnectionDTO, ShearConnectionPrioritizationDTO } from '@api/fastAPI.schemas'
import {
  getGetAllProjectShearConnectionsQueryKey,
  getGetProjectShearConnectionPrioritizationQueryKey,
  getGetShearConnectionUsageQueryKey,
  useDeleteProjectShearConnection,
  useGetAllProjectShearConnections,
  useGetProjectShearConnectionPrioritization,
  useGetShearAnchorStoreySelection,
  useGetShearConnectionUsage,
  usePostAddProjectShearConnection,
  usePostUpdateProjectShearConnection,
  useSetProjectShearConnectionPrioritization,
} from '@api/project-shear-connections/project-shear-connections'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { roofStoreyKey } from '@editorStores'
import { useModelStoreys, useResultsInvalidation } from '@editorHooks'
import { deletedConnectorMessage } from 'src/components/manager/connections/utils/deleteConfirmation'
import {
  listsWithoutActiveAnchor,
  prioritizationWithoutAnchor,
  reactivatedAnchorWarningMessage,
  ShearConnectionList,
  ShearConnectionPrioritizationLists,
  toShearConnectionCreate,
} from 'src/components/manager/shearConnections'
import { buildErrorMessage } from 'src/constants/errors'

/** The project's own Schubverbinder copy, seeded from the organisation library. */
const ShearConnectionManagerForm = (): ReactElement => {
  const { projectId } = useParams()
  const projectGuid = projectId as string
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const invalidateResults = useResultsInvalidation()

  const invalidateConnections = useCallback(
    () => queryClient.invalidateQueries(getGetAllProjectShearConnectionsQueryKey(projectGuid)),
    [queryClient, projectGuid],
  )
  const invalidatePrioritization = useCallback(
    () =>
      queryClient.invalidateQueries(
        getGetProjectShearConnectionPrioritizationQueryKey(projectGuid),
      ),
    [queryClient, projectGuid],
  )
  // A changed Schubverbinder or priority list changes which one each Schubanker gets.
  const invalidateUsage = useCallback(
    () => queryClient.invalidateQueries(getGetShearConnectionUsageQueryKey(projectGuid)),
    [queryClient, projectGuid],
  )

  // Fresh on mount: picks and results change on the results page.
  const { data: connectionUsage } = useGetShearConnectionUsage(projectGuid, {
    query: {
      refetchOnMount: true,
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Verwendung der Schubanker'),
          { variant: 'error' },
        ),
    },
  })
  const usageByGuid = useMemo(
    () => keyBy(connectionUsage ?? [], usage => usage.connectionGuid),
    [connectionUsage],
  )

  const allStoreys = useModelStoreys()
  const storeys = useMemo(
    () => allStoreys.filter(storey => storey !== roofStoreyKey).map(toNumber),
    [allStoreys],
  )
  // The per-storey anchor selection of the results view, only read here to
  // predict the backend's self-repair: an edit that leaves a storey list
  // without any active anchor re-activates the list's first one, and the
  // user is told so. Fresh on mount because it is edited on another page.
  const { data: storeySelection } = useGetShearAnchorStoreySelection(projectGuid, {
    query: {
      refetchOnMount: true,
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Schubankerauswahl'), {
          variant: 'error',
        })
      },
    },
  })

  const warnAboutReactivatedAnchors = useCallback(
    (nextPrioritization: ShearConnectionPrioritizationDTO) => {
      if (!storeySelection) return
      const labels = listsWithoutActiveAnchor(storeySelection, nextPrioritization, storeys)
      if (labels.length > 0) {
        enqueueSnackbar(reactivatedAnchorWarningMessage(labels), { variant: 'warning' })
      }
    },
    [storeySelection, storeys, enqueueSnackbar],
  )

  const { data: connections, isLoading } = useGetAllProjectShearConnections<
    ShearConnectionDTO[],
    AxiosError
  >(projectGuid, {
    query: {
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Schubverbinder'), {
          variant: 'error',
        })
      },
    },
  })

  const { data: prioritization, isLoading: isLoadingPrioritization } =
    useGetProjectShearConnectionPrioritization<ShearConnectionPrioritizationDTO, AxiosError>(
      projectGuid,
      {
        query: {
          onError: error => {
            enqueueSnackbar(
              buildErrorMessage(error, 'Fehler beim Laden der Schubverbinderpriorisierung'),
              { variant: 'error' },
            )
          },
        },
      },
    )

  const { mutateAsync: addConnection, isLoading: isCreating } = usePostAddProjectShearConnection<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Schubverbinder anlegen',
        description: 'Legt einen Schubverbinder im Projekt an',
      },
      onSuccess: async () => {
        await invalidateConnections()
        invalidateResults(projectGuid)
        enqueueSnackbar('Schubverbinder erfolgreich angelegt', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Schubverbinders'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutateAsync: updateConnection, isLoading: isEditing } =
    usePostUpdateProjectShearConnection<AxiosError, unknown>({
      mutation: {
        meta: {
          title: 'Schubverbinder speichern',
          description: 'Speichert die Änderungen an einem Schubverbinder des Projekts',
        },
        onSuccess: async () => {
          await invalidateConnections()
          await invalidateUsage()
          invalidateResults(projectGuid)
          enqueueSnackbar('Schubverbinder erfolgreich gespeichert', { variant: 'success' })
        },
        onError: error => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Schubverbinders'), {
            variant: 'error',
          })
        },
      },
    })

  const { mutate: deleteConnection } = useDeleteProjectShearConnection<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Schubverbinder löschen',
        description: 'Löscht einen Schubverbinder aus dem Projekt',
      },
      onSuccess: async (result, { connectionGuid }) => {
        await invalidateConnections()
        // Deleting a connector also drops it from every prioritization list, and resets the
        // Schubanker fixed to it.
        await invalidatePrioritization()
        await invalidateUsage()
        invalidateResults(projectGuid)
        enqueueSnackbar(
          deletedConnectorMessage(result?.resetUserDefinedAnchorCount ?? 0, 'shear'),
          { variant: 'success' },
        )
        if (prioritization) {
          warnAboutReactivatedAnchors(prioritizationWithoutAnchor(prioritization, connectionGuid))
        }
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Schubverbinders'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: savePrioritization } = useSetProjectShearConnectionPrioritization<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Schubverbinderpriorisierung speichern',
        description: 'Speichert die Reihenfolge der Schubverbinder pro Geschosskategorie',
      },
      onSuccess: async (_data, { data: nextPrioritization }) => {
        await invalidatePrioritization()
        await invalidateUsage()
        invalidateResults(projectGuid)
        enqueueSnackbar('Schubverbinderpriorisierung erfolgreich gespeichert', {
          variant: 'success',
        })
        warnAboutReactivatedAnchors(nextPrioritization)
      },
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Schubverbinderpriorisierung'),
          { variant: 'error' },
        )
      },
    },
  })

  if (isLoading || isLoadingPrioritization || !connections || !prioritization) {
    return <LoadingIndicator />
  }

  return (
    <Stack direction="column" spacing={5}>
      <ShearConnectionList
        connections={connections}
        usageByGuid={usageByGuid}
        isProjectLibrary
        isCreating={isCreating}
        isEditing={isEditing}
        onCreate={values => addConnection({ projectGuid, data: toShearConnectionCreate(values) })}
        onEdit={values =>
          updateConnection({
            projectGuid,
            connectionGuid: values.guid,
            data: toShearConnectionCreate(values),
          })
        }
        onDelete={({ guid }) => deleteConnection({ projectGuid, connectionGuid: guid })}
      />

      <ShearConnectionPrioritizationLists
        connections={connections}
        value={prioritization}
        onChange={next => savePrioritization({ projectGuid, data: next })}
      />
    </Stack>
  )
}

export default ShearConnectionManagerForm
